import CHARM_VAULT_ABI from 'abis/CharmVault.json'
import MULTICALL3_ABI from 'abis/multicall3.json'
import WETH_ABI from 'abis/weth.json'
import { MULTICALL_3_ADDRESSES } from 'constants/addresses'
import { nativeOnChain } from 'constants/tokens'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi, Hex, PublicClient, WriteContractReturnType } from 'viem'
import { Address, encodeFunctionData } from 'viem'

import {
  DoubleSidedParams,
  SingleSidedParams,
  StakeParams,
  UnstakeParams,
  VaultStrategy,
  WithdrawParams,
} from './VaultStrategy.type'

export class CharmVaultStrategy implements VaultStrategy {
  readonly supportsZapping = true
  readonly supportsAutoCompound = true
  transactionType!: TransactionType

  constructor(
    private charmVaultAddress: Address,
    private wagmiPublicClient: PublicClient,
    private writeContractAsync: (args: {
      address: Address
      abi: Abi
      functionName: string
      args?: any[]
      value?: bigint
      gas?: bigint
      account: Address
    }) => Promise<WriteContractReturnType>,
    private chainId: number
  ) {}

  stakeLiquidity?(params: StakeParams): Promise<Hex> {
    throw new Error('Method not implemented.')
  }
  unstakeLiquidity?(params: UnstakeParams): Promise<Hex> {
    throw new Error('Method not implemented.')
  }

  get routerAddress(): string {
    // Charm doesn't use routers - we interact directly with the vault contract
    return this.charmVaultAddress
  }

  // ========================================
  // DOUBLE-SIDED LIQUIDITY (Direct to CharmVault)
  // ========================================
  async addLiquidityDoubleSided(params: DoubleSidedParams): Promise<Hex> {
    const { currencyA, currencyB } = params

    // Check if either token is native BERA
    const isTokenANative = currencyA.isNative
    const isTokenBNative = currencyB.isNative

    try {
      // Case 1: Both tokens are ERC20 (original logic)
      if (!isTokenANative && !isTokenBNative) {
        return await this.depositERC20Only(params)
      }

      // Case 2: One or both tokens are native BERA
      return await this.depositWithNativeToken(params, isTokenANative, isTokenBNative)
    } catch (error: any) {
      console.error('Gas estimation failed for CharmVault deposit:', error)
      throw error
    }
  }

  private async depositERC20Only(params: DoubleSidedParams): Promise<Hex> {
    const { parsedAmountA, parsedAmountB, amount0Min, amount1Min, account } = params

    try {
      const tx = {
        address: this.charmVaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'deposit',
        args: [
          BigInt(parsedAmountA.quotient.toString()),
          BigInt(parsedAmountB.quotient.toString()),
          BigInt(amount0Min),
          BigInt(amount1Min),
          account as Address,
        ],
        account: account as Address,
      } as const

      const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)

      const txHash = await this.writeContractAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    } catch (error: any) {
      console.error('Gas estimation failed for ERC20 deposit:', error)
      throw error
    }
  }

  private async depositWithNativeToken(
    params: DoubleSidedParams,
    isTokenANative: boolean,
    isTokenBNative: boolean
  ): Promise<Hex> {
    const { parsedAmountA, parsedAmountB, account } = params

    const multicall3Address = MULTICALL_3_ADDRESSES[this.chainId] as Address
    const wberaAddress = this.getWNativeAddress() as Address

    let totalBERAValue = 0n
    const calls: Array<{
      target: Address
      allowFailure: boolean
      value: bigint
      callData: Hex
    }> = []

    // Calculate total BERA value needed
    if (isTokenANative) {
      totalBERAValue += BigInt(parsedAmountA.quotient.toString())
    }
    if (isTokenBNative) {
      totalBERAValue += BigInt(parsedAmountB.quotient.toString())
    }

    try {
      // Step 1: Wrap native BERA to WBERA
      if (totalBERAValue > 0n) {
        const wrapCalldata = encodeFunctionData({
          abi: WETH_ABI as Abi,
          functionName: 'deposit',
        })
        calls.push({
          target: wberaAddress,
          allowFailure: false,
          value: totalBERAValue,
          callData: wrapCalldata,
        })

        // Step 2: Approve WBERA to Charm vault
        const approveCalldata = encodeFunctionData({
          abi: WETH_ABI as Abi,
          functionName: 'approve',
          args: [this.charmVaultAddress, totalBERAValue],
        })
        calls.push({
          target: wberaAddress,
          allowFailure: false,
          value: 0n,
          callData: approveCalldata,
        })
      }

      // Note: Deposit call is commented out in original - keeping same behavior
      // Step 3: Deposit to Charm vault would go here if needed

      // Execute multicall3
      const tx = {
        address: multicall3Address,
        abi: MULTICALL3_ABI as Abi,
        functionName: 'aggregate3Value',
        args: [calls],
        value: totalBERAValue,
        account: account as Address,
      } as const

      const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)

      const txHash = await this.writeContractAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    } catch (error: any) {
      console.error('Multicall3 deposit failed:', error)
      throw error
    }
  }
  // Helper methods
  private getWNativeAddress(): string {
    return nativeOnChain(this.chainId).wrapped.address
  }

  // ========================================
  // SINGLE-SIDED LIQUIDITY (Via KX Router)
  // ========================================
  async addLiquiditySingleSided(params: SingleSidedParams): Promise<Hex> {
    throw new Error('Must be implemented by KX Router')
  }

  // ========================================
  // NATIVE TOKEN LIQUIDITY (Via KX Router)
  // ========================================
  addLiquidityNative(): Promise<Hex> {
    throw new Error('Must be implemented by KX Router')
  }

  // ========================================
  // WITHDRAW LIQUIDITY
  // ========================================
  async withdrawLiquidity(params: WithdrawParams): Promise<Hex> {
    const { amount, amount0Min, amount1Min, account } = params

    try {
      const tx = {
        address: this.charmVaultAddress,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'withdraw',
        args: [BigInt(amount.quotient.toString()), BigInt(amount0Min), BigInt(amount1Min), account as Address],
        account: account as Address,
      } as const

      const estimatedGas = await this.wagmiPublicClient.estimateContractGas(tx)

      const txHash = await this.writeContractAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    } catch (error: any) {
      console.error('Gas estimation failed for CharmVault withdrawal:', error)
      throw error
    }
  }
}
